import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Instance } from '../types';
import './CustomerModal.css';

interface InstanceEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  instance: Instance;
  onSave: () => void;
}

export const InstanceEditModal = ({
  isOpen,
  onClose,
  instance,
  onSave,
}: InstanceEditModalProps) => {
  const [name, setName] = useState(instance.name);
  const [email, setEmail] = useState(instance.email);
  const [qbdPath, setQbdPath] = useState(instance.qbdPath);
  const [appUrl, setAppUrl] = useState(instance.appUrl);
  const [frequencyInMinutes, setFrequencyInMinutes] = useState(instance.frequencyInMinutes);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(instance.name);
      setEmail(instance.email);
      setQbdPath(instance.qbdPath);
      setAppUrl(instance.appUrl);
      setFrequencyInMinutes(instance.frequencyInMinutes);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, instance]);

  const handleSubmit = async () => {
    if (!name || !email || !qbdPath || !appUrl || !frequencyInMinutes) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const updates: Partial<Instance> = {};
      if (name !== instance.name) updates.name = name;
      if (email !== instance.email) updates.email = email;
      if (qbdPath !== instance.qbdPath) updates.qbdPath = qbdPath;
      if (appUrl !== instance.appUrl) updates.appUrl = appUrl;
      if (frequencyInMinutes !== instance.frequencyInMinutes) updates.frequencyInMinutes = frequencyInMinutes;

      await api.updateInstance(String(instance.id), updates);

      setSuccess(true);
      setTimeout(() => {
        onClose();
        onSave();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update instance');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Instance</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {success ? (
            <div className="success-message">
              <div className="success-icon">✓</div>
              <p>Instance updated successfully!</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="alert-error">
                  {error}
                </div>
              )}

              <div className="create-customer-form">
                <div className="form-group">
                  <label>
                    Instance Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="My Instance"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="user@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>
                    QuickBooks File Path <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="C:\Company\QuickBooks\CompanyFile.qbw"
                    value={qbdPath}
                    onChange={(e) => setQbdPath(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>
                    Application URL <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="https://example.com"
                    value={appUrl}
                    onChange={(e) => setAppUrl(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>
                    Sync Frequency (minutes) <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="30"
                    min={1}
                    value={frequencyInMinutes}
                    onChange={(e) => setFrequencyInMinutes(Number(e.target.value))}
                  />
                </div>

                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
